import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { Spinner } from "reactstrap";
import {
  ObjectHelper,
  CommonBackendHelper,
  FileHelper,
} from "@workspace/common";
import "./OfferCreation.scss";
import { offerStatus } from "@workspace/job/src/store/actions";
import { JOB_STAGE_STATUS } from "@workspace/job/src/components/JobListing/JobListingConstants";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import {
  createJobDocument,
  deleteJobDocument,
} from "@workspace/job/src/helpers/backend_helper";
import { CREATE_OFFER_ELC } from "../ELCTable/constants";

const OfferCreation = forwardRef(
  (
    { offerData, edit, correction, setOfferUploaded, setLoading },
    parentRef
  ) => {
    const dispatch = useDispatch();

    const [totalUploadCount, setTotalUploadCount] = useState(0);
    const [fileObjects, setFileObjects] = useState([]);
    const [fileUrl, setFileUrl] = useState("");
    const [submitType, setSubmitType] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
      setLoading(false);
      if (correction) {
        setOfferUploaded(true);
      } else {
        setOfferUploaded(false);
      }
    }, []);

    useEffect(() => {
      if (edit) {
        try {
          setPreviewLoading(true);
          CommonBackendHelper.downloadDocumentByEntityAndId({
            entityId: offerData?.id,
            entityType: CREATE_OFFER_ELC,
          }).then((response) => {
            const documentData = response?.data;

            if (!documentData || !documentData.encodedFile) {
              toast.error(`File cannot be found!`);
              return;
            }

            const file = FileHelper.base64ToFile(
              documentData?.encodedFile,
              documentData?.fileName
            );
            const url = URL.createObjectURL(file);
            setFileUrl(url);

            const fileObjStore = [];
            const newFileObj = {
              name: documentData?.fileName,
              file: file,
              nameNoExt: documentData?.fileName
                .split(".")
                .slice(0, -1)
                .join("."),
            };
            fileObjStore.push(newFileObj);
            setFileObjects(fileObjStore);
          });
        } catch (e) {
          toast.error("Failed to preview!");
        } finally {
          setPreviewLoading(false);
        }
      }
    }, [edit, offerData]);

    // Delete file that was selected for parsing
    const deleteFile = (index) => {
      setFileObjects(fileObjects.filter((file, i) => i !== index));
      setOfferUploaded(false);

      if (fileObjects.length === 1) {
        setFileUrl("");
        setTotalUploadCount(0);
      }
    };

    useImperativeHandle(parentRef, () => ({
      deleteFile: (index) => {
        deleteFile(index);
      },
      submitForm: (type) => {
        handleSubmitForm(type);
      },
    }));

    const handleSubmitForm = (type) => {
      setSubmitType(type);
      if (formik.isValid) {
        formik.handleSubmit();
      } else {
        toastErrors();
      }
    };

    // Need to be changed for Offer Creation instead of Conditional Offer
    const handleFormSubmit = async (values) => {
      if (fileObjects?.length === 0) {
        toast.error("Please upload an offer.");
        return;
      }
      setLoading(true);
      const fileOb = fileObjects?.[0]?.file;
      let formValues = { ...values, file: fileOb };
      const documentData = {
        ...formValues,
        title: "Offer",
        documentKey: CREATE_OFFER_ELC,
        entityType: CREATE_OFFER_ELC,
        entityId: parseInt(offerData?.id),
        formData: JSON.stringify(formValues),
        formId: parseInt(offerData?.id),
      };

      const documentFormData =
        ObjectHelper.convertObjectToFormData(documentData);

      await deleteJobDocument(CREATE_OFFER_ELC, parseInt(offerData?.id));

      const response = await createJobDocument(documentFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setLoading(false);

      if (submitType === "draft") {
        // Payload for Draft
        const payload = {
          preOfferId: offerData?.id,
          formData: JSON.stringify(response?.data),
          formId: null,
          status: JOB_STAGE_STATUS.DRAFT,
          offerType: "create_offer",
        };
        dispatch(offerStatus(payload));
      } else if (submitType === "submit") {
        // Payload for Submit
        const payload = {
          preOfferId: offerData?.id,
          formData: JSON.stringify(response?.data),
          formId: null,
          status: JOB_STAGE_STATUS.SUBMITTED,
          offerType: "create_offer",
        };
        dispatch(offerStatus(payload));
      }
    };

    const toastErrors = () => {
      if (formik.errors) {
        Object.keys(formik.errors).forEach((key) => {
          toast.error(formik.errors[key]);
        });
      }
    };

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        content: "",
      },
      validateOnBlur: true,
      onSubmit: handleFormSubmit,
    });

    const setFileObj = (files) => {
      const fileObjStore = [];
      for (let i = 0; i < files.length; i++) {
        const newFileObj = {
          name: files[i].name,
          file: files[i],
          nameNoExt: files[i].name.split(".").slice(0, -1).join("."),
        };
        fileObjStore.push(newFileObj);
      }
      setFileObjects(fileObjStore);
    };

    // Check if file extension is correct (doc, docx or pdf). If not, display error message
    const checkFileExtension = (file) => {
      // Check if file extension is correct
      const fileType = file.name.split(".").pop().toLowerCase();
      if (fileType !== "pdf") {
        toast.error("Only PDF file is allowed.");
        return false;
      }
      return true;
    };

    /**
     * Dropzone function to handle file upload
     */
    const onDrop = useCallback((acceptedFiles) => {
      // Do something with the files
      setTotalUploadCount(0);
      setFileObjects([]);

      const files = acceptedFiles;
      // check if file exist
      if (files.length === 0) {
        toast.error("Please select a file before uploading.");
        return;
      }

      if (files.length > 1) {
        toast.error("Only one file is allowed to upload.");
        return;
      }

      // Check all files extension
      for (let i = 0; i < files.length; i++) {
        if (!checkFileExtension(files[i])) return;
      }

      setTotalUploadCount(files.length);

      // Set file object
      setFileObj(files);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
    });

    // This method is to handle large file size.
    const setFileUrlFunc = (index, type, files = null) => {
      const ext = fileObjects[index].name.split(".").pop().toLowerCase();
      // If docx then ignore
      if (ext === "docx" || ext === "doc") {
        // Clear the reader
        setFileUrl("");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // Create a Blob from the base64 data
        const base64Data = reader.result.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create a Blob URL
        const blobUrl = URL.createObjectURL(blob);
        setFileUrl(blobUrl);
      };
      if (type === 2) {
        reader.readAsDataURL(fileObjects[index].file);
      } else {
        reader.readAsDataURL(files[0]);
      }
    };

    useEffect(() => {
      if (fileObjects.length > 0) {
        setTotalUploadCount(fileObjects.length);
        setFileUrlFunc(0, 2);
        setOfferUploaded(true);
      }
    }, [fileObjects]);

    if (previewLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner size="md" color="primary" />
        </div>
      );
    } else {
      return (
        <div
          style={{
            borderTop: "1px solid #e9ecef",
          }}
        >
          {fileObjects.length === 0 ? (
            <div
              {...getRootProps()}
              className="d-flex flex-column justify-content-center align-items-center cursor-pointer mt-4 mx-4"
              style={{ border: "2px dashed grey", height: "275px" }}
            >
              <input {...getInputProps()} />
              {totalUploadCount === 0 && (
                <div>
                  <i
                    className="ri-file-upload-line"
                    style={{ fontSize: "4rem" }}
                  ></i>
                </div>
              )}
              {totalUploadCount === 0 &&
                (isDragActive ? (
                  <p>Drop the offer here ...</p>
                ) : (
                  <div className="text-center">
                    <p className="mb-0">
                      <strong>Drag 'n' drop</strong> some offer here, or{" "}
                      <strong>click</strong> to select offer.
                    </p>
                    <p>
                      Only <strong>PDF </strong> file is allowed
                    </p>
                  </div>
                ))}
              <div className="text-center">
                {totalUploadCount > 0 && (
                  <span
                    style={{ fontSize: "1.2rem" }}
                  >{`${totalUploadCount} file attached`}</span>
                )}
              </div>
            </div>
          ) : (
            <>
              {fileUrl !== "" ? (
                <object
                  data={fileUrl}
                  type="application/pdf"
                  width="100%"
                  height="575px"
                ></object>
              ) : (
                <div
                  className="text-center"
                  style={{ fontSize: "1rem", marginTop: "30%" }}
                >
                  File cannot be displayed
                </div>
              )}
            </>
          )}
        </div>
      );
    }
  }
);

export default OfferCreation;
